import { useEventDataFilters } from '~/composables/events/useEventDataFilters/useEventDataFilters';
import { useTrackSearchPropertyEvents } from '~/composables/events/useTrackSearchPropertyEvents';
import { MixpanelProps } from '~/core/service/mixpanel';
import { useShortsFilterChangeStore } from '~/stores/shortsFilterChangeStore';
import { nextTick, watch } from 'vue';

interface FilterRef {
	value: number | null | undefined;
}

interface UseShortsFilterChangeTrackingOptions {
	isFetchingProperties: () => boolean;
	countProperties: () => number;
	minPrice?: FilterRef;
	maxPrice?: FilterRef;
	minArea?: FilterRef;
	maxArea?: FilterRef;
	toShortsFilters: () => any;
}

/**
 * Composable para rastrear mudanças nos filtros de Shorts
 * e disparar eventos de analytics quando os filtros são aplicados.
 * O estado de mudanças é mantido em uma Pinia store (client-only).
 */
export function useShortsFilterChangeTracking(
	options: UseShortsFilterChangeTrackingOptions
) {
	const filterChangeStore = useShortsFilterChangeStore();

	// Watch individual price filters
	if (options.minPrice) {
		watch(
			() => options.minPrice!.value,
			() => {
				filterChangeStore.addChangedKey('minAskingPrice');
			}
		);
	}

	if (options.maxPrice) {
		watch(
			() => options.maxPrice!.value,
			() => {
				filterChangeStore.addChangedKey('maxAskingPrice');
			}
		);
	}

	// Watch individual area filters
	if (options.minArea) {
		watch(
			() => options.minArea!.value,
			() => {
				filterChangeStore.addChangedKey('minArea');
			}
		);
	}

	if (options.maxArea) {
		watch(
			() => options.maxArea!.value,
			() => {
				filterChangeStore.addChangedKey('maxArea');
			}
		);
	}

	// Watch fetching status and dispatch event when done
	watch(
		() => options.isFetchingProperties(),
		async isFetching => {
			if (!isFetching && filterChangeStore.hasChanges()) {
				// Wait for DOM updates
				await nextTick();

				// Re-check fetching status to prevent race conditions where a new
				// fetch starts before this async callback executes
				if (options.isFetchingProperties()) {
					return;
				}

				handleEventsFilterOnCloseField();
			}
		}
	);

	function handleEventsFilterOnCloseField() {
		if (!filterChangeStore.hasChanges()) return;

		const currentFilters = options.toShortsFilters();
		const currentPf = currentFilters as any;
		const arrayChangedKey = filterChangeStore.getChangedKeys();

		const { getFilterAppliedValues } = useEventDataFilters();
		const { trackFilterApplied } = useTrackSearchPropertyEvents();

		const values = getFilterAppliedValues(
			currentPf,
			'openFilters',
			arrayChangedKey
		);
		values[MixpanelProps.TOTAL_RESULTS] = options.countProperties();
		trackFilterApplied(values as any);
		filterChangeStore.clear();
	}
}
